"use client"
import { useEffect, useRef, useState } from "react"
import { useAnimationTuning } from "@/app/playbook/animation/animationTuningContext"

// Integration guard: a long frame gap (tab hidden, main-thread hitch)
// integrates as several small steps instead of one unstable big one.
const MAX_SPRING_STEP_S = 1 / 30
// Done when within an hour of the target and drifting less than a day/s.
const SETTLE_POSITION_EPSILON_MS = 60 * 60 * 1000
const SETTLE_VELOCITY_EPSILON_MS_PER_S = 24 * 60 * 60 * 1000

// Smoothly animate the HUD date so panels move between jumps. The playbook
// scrolls/cameras between keyframes that can sit months or years apart; if
// downstream panels (policies, graphs, charts, timeline cursor) read the raw
// target date they'd snap discontinuously on every keyframe change.
//
// The motion is a critically damped spring rather than a fixed-duration
// ease: a fresh jump starts at ZERO velocity (no first-frame teleport even
// if the jump-moment React commit drops a frame — the fastest motion is
// mid-flight, not at t=0), it settles in ≈ HUD_SYNC_TRANSITION_MS, and
// velocity carries across retargets, so continuous scroll scrubbing tracks
// fluidly instead of restarting an easing curve every frame. Works
// identically forwards and backwards: unlike the old fixed-duration tween —
// which snapped instantly on any backward jump over two years — the spring's
// settle time is distance-independent, so even a whole-timeline rewind is a
// single ~500ms glide.
export const useAnimatedDate = (currentDate: Date) => {
  const [animatedDate, setAnimatedDate] = useState(currentDate)
  const positionRef = useRef(currentDate.getTime())
  const velocityRef = useRef(0)
  const targetRef = useRef(currentDate.getTime())
  const animationFrameRef = useRef<number | null>(null)
  const lastFrameTimeRef = useRef<number | null>(null)

  // Live-tunable spring stiffness (see animationTuningContext): a critically
  // damped step response reaches ~settleFactor∕ω of its target, so the spring
  // settles in ≈ hudSyncTransitionMs. A ref so the running rAF loop reads the
  // latest value without restarting. Defaults to the saved JSON everywhere;
  // overridden only inside /hud-studio.
  const tuning = useAnimationTuning()
  const omegaPerSecondRef = useRef(0)
  omegaPerSecondRef.current =
    tuning.dateSpringSettleFactor / (tuning.hudSyncTransitionMs / 1000)

  useEffect(() => {
    const targetValue = currentDate.getTime()
    if (targetRef.current === targetValue) return
    targetRef.current = targetValue

    if (animationFrameRef.current !== null) return // spring already running

    const runSpring = (timestamp: number) => {
      const frameSeconds = lastFrameTimeRef.current === null
        ? 1 / 60
        : Math.min(0.25, (timestamp - lastFrameTimeRef.current) / 1000)
      lastFrameTimeRef.current = timestamp

      // Semi-implicit Euler, sub-stepped so long frames stay stable.
      let remainingSeconds = frameSeconds
      while (remainingSeconds > 0) {
        const stepSeconds = Math.min(remainingSeconds, MAX_SPRING_STEP_S)
        remainingSeconds -= stepSeconds
        const omegaPerSecond = omegaPerSecondRef.current
        const displacement = positionRef.current - targetRef.current
        const acceleration =
          -omegaPerSecond * omegaPerSecond * displacement
          - 2 * omegaPerSecond * velocityRef.current
        velocityRef.current += acceleration * stepSeconds
        positionRef.current += velocityRef.current * stepSeconds
      }

      const isSettled =
        Math.abs(positionRef.current - targetRef.current) < SETTLE_POSITION_EPSILON_MS
        && Math.abs(velocityRef.current) < SETTLE_VELOCITY_EPSILON_MS_PER_S
      if (isSettled) {
        positionRef.current = targetRef.current
        velocityRef.current = 0
        lastFrameTimeRef.current = null
        animationFrameRef.current = null
        setAnimatedDate(new Date(targetRef.current))
        return
      }
      setAnimatedDate(new Date(Math.round(positionRef.current)))
      animationFrameRef.current = requestAnimationFrame(runSpring)
    }
    animationFrameRef.current = requestAnimationFrame(runSpring)
  }, [currentDate])

  // Unmount cleanup only: the spring loop itself survives target changes.
  // Must ALSO null the ref — under StrictMode's setup→cleanup→setup cycle a
  // stale id would otherwise convince the "loop already running" guard above
  // to never start a spring again, freezing the date permanently.
  useEffect(() => () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
      lastFrameTimeRef.current = null
    }
  }, [])

  return animatedDate
}
